import { Navigate, useSearchParams } from "react-router-dom";
import { Crown } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Page, PageHead } from "@/components/hub/Common";
import { Overview } from "@/components/admin/Overview";
import { UsersTab } from "@/components/admin/UsersTab";
import { WorkspacesTab } from "@/components/admin/WorkspacesTab";
import { ReleasesTab } from "@/components/admin/ReleasesTab";
import { SuggestionsTab } from "@/components/admin/SuggestionsTab";
import { SupportTab } from "@/components/admin/SupportTab";
import { GalleryTab } from "@/components/admin/GalleryTab";
import { ChallengesTab } from "@/components/admin/ChallengesTab";
import { EmailTab } from "@/components/admin/EmailTab";
import { PromosTab } from "@/components/admin/PromosTab";
import { AdsTab } from "@/components/admin/AdsTab";
import { StripeTab } from "@/components/admin/StripeTab";
import { CleanupTab } from "@/components/admin/CleanupTab";
import { RealStatsTab } from "@/components/admin/RealStatsTab";
import { HolidaysTab } from "@/components/admin/HolidaysTab";
import { AlertsTab, useRateAlerts } from "@/components/admin/AlertsTab";

const TABS = [
  ["overview", Overview], ["real", RealStatsTab], ["users", UsersTab], ["workspaces", WorkspacesTab],
  ["releases", ReleasesTab], ["suggestions", SuggestionsTab], ["support", SupportTab], ["gallery", GalleryTab], ["challenges", ChallengesTab], ["promos", PromosTab], ["holidays", HolidaysTab], ["stripe", StripeTab], ["ads", AdsTab], ["email", EmailTab], ["alerts", AlertsTab], ["cleanup", CleanupTab],
];

export default function MasterCenter() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [alerts] = useRateAlerts();
  const pending = (alerts || []).filter((a) => !a.contacted_at || a.contacted_at < a.last_at).length;
  const saved = localStorage.getItem("clipx_admin_tab");
  const tab = params.get("tab") || (TABS.some(([k]) => k === saved) ? saved : "overview");
  const pick = (k) => {
    localStorage.setItem("clipx_admin_tab", k);
    if (new URLSearchParams(window.location.search).get("tab") !== k) setParams({ tab: k });
  };
  if (!user?.is_superadmin) return <Navigate to="/dashboard" replace />;
  return (
    <Page>
      <PageHead label={<span className="flex items-center gap-2"><Crown className="w-3.5 h-3.5 text-[#ffc94d]" />{t("adm.label")}</span>} t1={t("adm.title1")} t2={t("adm.title2")} sub={t("adm.sub")} />
      <Tabs value={tab} onValueChange={pick} className="mt-8" data-testid="master-center">
        <TabsList className="bg-white/[0.03] border border-white/5 p-1 h-auto flex flex-wrap justify-start gap-1 rounded-xl">
          {TABS.map(([k]) => (
            <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`}
              className="rounded-lg px-4 py-2 text-sm text-slate-400 data-[state=active]:bg-[#2f7bff]/20 data-[state=active]:text-white data-[state=active]:shadow-[0_0_20px_-6px_rgba(47,123,255,0.8)]">{t(`adm.tab_${k}`)}{k === "alerts" && pending > 0 && <span data-testid="admin-alerts-badge" className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold">{pending}</span>}</TabsTrigger>
          ))}
        </TabsList>
        {TABS.map(([k, C]) => <TabsContent key={k} value={k} className="mt-6 cx-page-in"><C /></TabsContent>)}
      </Tabs>
    </Page>
  );
}
